import {
  Badge,
  Box,
  HStack,
  Icon,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { WarningIcon } from "@chakra-ui/icons";
import { FaMapMarkerAlt } from "react-icons/fa";
import {
  parseClockLocation,
  isMobileClockType,
  checkLocationBounds,
} from "../utils/location-parser";
import { ClockLocationModalContent } from "./ClockLocationModal";

interface ClockLocationCellProps {
  location: any;
  clockType: string | null;
  visitType: "clockin" | "clockout";
  patientId: number;
  patientAddressLocation: any; // Will be {lat: number, lng: number} | null after GraphQL types regenerate
}

export function ClockLocationCell({
  location,
  clockType,
  visitType,
  patientId,
  patientAddressLocation,
}: ClockLocationCellProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const parsedLocation = parseClockLocation(location);
  const isMobile = isMobileClockType(clockType);

  // If no location data
  if (!parsedLocation) {
    return <span>-</span>;
  }

  // If not mobile type, just show dash or type
  if (!isMobile) {
    return <span>{clockType || "-"}</span>;
  }

  // Check if location is out of bounds by comparing with patient address
  const locationWithBounds = checkLocationBounds(parsedLocation, patientAddressLocation);

  return (
    <>
      <HStack spacing={2}>
        {/* Map icon - clickable to open modal */}
        <Tooltip label="View on map" placement="top">
          <Box
            _hover={{ opacity: 0.7 }}
            as="button"
            cursor="pointer"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            <Icon as={FaMapMarkerAlt} boxSize={4} color="blue.500" />
          </Box>
        </Tooltip>

        {/* Red badge if out of bounds */}
        {locationWithBounds.outOfBounds && (
          <Tooltip
            label={`Out of bounds${
              locationWithBounds.distance
                ? ` (${locationWithBounds.distance.toFixed(2)} mi away)`
                : ""
            }`}
            placement="top"
          >
            <Badge alignItems="center" colorScheme="red" display="flex" fontSize="xs" gap={1}>
              <Icon as={WarningIcon} boxSize={2} />
              Out
            </Badge>
          </Tooltip>
        )}
      </HStack>

      {/* Location modal - only render when open */}
      {isOpen && (
        <Modal isOpen={isOpen} size="xl" onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{visitType === "clockin" ? "Clock In" : "Clock Out"} Location</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <ClockLocationModalContent
                location={locationWithBounds}
                patientAddressLocation={patientAddressLocation}
                patientId={patientId}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
